/* eslint-disable jira/filename-case */
import keyBy from 'lodash/keyBy';
import chunk from 'lodash/chunk';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { runInBatch } from '@jira/polaris__lib-run-in-batch/src/index.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import {
	isBadRequestError,
	isNotFoundError,
} from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { performGetIssueTypesForProjects } from './JiraperformGetIssueTypesForProjects.types.tsx';
import type { State } from './IssueType.types.tsx';
import { transformIssueTypeResponse } from './isUpdatingRemovedIssueTypeError.tsx';

type ProjectId = string;

const PROJECT_IDS_LIMIT = 50;
const BATCH_SIZE = 5;

const isPreconditionFailedError = (error: Error) =>
	'statusCode' in error && error.statusCode === 412;

export const fetchIssueTypesForProjects =
	(projectIds: ProjectId[]) =>
	async ({ setState, getState }: StoreActionApi<State>): Promise<void> => {
		experience.issueTypes.issueTypesForProjectsLoad.start();
		const prevState = getState();

		try {
			setState({
				issueTypesByProjectId: {
					...prevState.issueTypesByProjectId,
					...projectIds.reduce<Record<ProjectId, string[]>>((acc, projectId) => {
						acc[projectId] = prevState.issueTypesByProjectId[projectId] ?? [];
						return acc;
					}, {}),
				},
			});

			const projectIdsChunks = chunk(projectIds, PROJECT_IDS_LIMIT);
			const batchResponse = await runInBatch(
				projectIdsChunks.map((ids) => () => performGetIssueTypesForProjects(ids)),
				BATCH_SIZE,
			);
			const projects = batchResponse.map((c) => c.projects).flat();

			const { issueTypesById, issueTypesByProjectId } = projects.reduce<
				Pick<State, 'issueTypesById' | 'issueTypesByProjectId'>
			>(
				(acc, { projectId, issueTypes }) => ({
					issueTypesById: {
						...acc.issueTypesById,
						...keyBy(issueTypes.map(transformIssueTypeResponse), ({ id }) => id),
					},
					issueTypesByProjectId: {
						...acc.issueTypesByProjectId,
						[projectId]: issueTypes.map(({ id }) => String(id)),
					},
				}),
				{
					issueTypesById: {},
					issueTypesByProjectId: {},
				},
			);

			setState({
				issueTypesById: {
					...getState().issueTypesById,
					...issueTypesById,
				},
				issueTypesByProjectId: {
					...getState().issueTypesByProjectId,
					...issueTypesByProjectId,
				},
			});

			experience.issueTypes.issueTypesForProjectsLoad.success();
		} catch (maybeError) {
			const error =
				maybeError instanceof Error
					? maybeError
					: new Error('Error while fetching issue types for projects');

			const shouldAbortError =
				isClientFetchError(error) ||
				// https://pi-dev-sandbox.atlassian.net/browse/POL-19127
				isNotFoundError(error) ||
				isBadRequestError(error) ||
				// https://pi-dev-sandbox.atlassian.net/browse/POL-20499
				isPreconditionFailedError(error);

			if (shouldAbortError) {
				experience.issueTypes.issueTypesForProjectsLoad.abort();
			} else {
				experience.issueTypes.issueTypesForProjectsLoad.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.fetch-issue-types-for-projects',
				'Failed to fetch issue types for projects',
				error,
			);

			throw error;
		}
	};
